import type {
    CensusCandidate,
    ExtractedPatientMatch,
    MatchDisposition,
    MatchProceedPlan,
    MatchVerificationRun
} from '@/types/meeting-transcription';
import {
    MATCH_NOT_IN_CENSUS_LABEL,
    MATCH_SELECT_PATIENT_LABEL,
    MATCH_SELECT_SOURCE_AI,
    MATCH_SELECT_SOURCE_DROPDOWN,
    MATCH_STATUS_CONFIRMED,
    MATCH_STATUS_HIGH_CONFIDENCE,
    MATCH_STATUS_UNCERTAIN
} from '@/utils/meeting-transcription/constants';

export const NOT_IN_CENSUS_VALUE = 'not-in-census';

export type MatchSelectSource = typeof MATCH_SELECT_SOURCE_AI | typeof MATCH_SELECT_SOURCE_DROPDOWN | typeof NOT_IN_CENSUS_VALUE | '';

export type MatchStatusChip = {
    label: string;
    color: 'success' | 'warning' | 'default';
};

export const getInitialDisposition = (match: ExtractedPatientMatch): MatchDisposition => {
    if (match.confidence === 'high' && match.matchedPatient) {
        return { status: 'confirmed', patientId: match.matchedPatient.id };
    }
    return { status: 'unverified' };
};

export const buildInitialDispositions = (matches: ExtractedPatientMatch[]): Record<string, MatchDisposition> =>
    Object.fromEntries(matches.map((match) => [match.id, getInitialDisposition(match)]));

export const dispositionFromRadioValue = (value: string): MatchDisposition => {
    if (!value) {
        return { status: 'unverified' };
    }
    if (value === NOT_IN_CENSUS_VALUE) {
        return { status: 'not-in-census' };
    }
    return { status: 'confirmed', patientId: value };
};

export const radioValueFromDisposition = (disposition: MatchDisposition): string => {
    if (disposition.status === 'confirmed') {
        return disposition.patientId;
    }
    if (disposition.status === 'not-in-census') {
        return NOT_IN_CENSUS_VALUE;
    }
    return '';
};

export const getMatchSelectOptions = (match: ExtractedPatientMatch): CensusCandidate[] => {
    const options: CensusCandidate[] = [];
    const seen = new Set<string>();
    const push = (candidate?: CensusCandidate) => {
        if (!candidate || seen.has(candidate.id)) {
            return;
        }
        seen.add(candidate.id);
        options.push(candidate);
    };
    push(match.matchedPatient);
    match.candidates.forEach(push);
    return options;
};

export const getMatchSelectSource = (value: string, aiOptions: CensusCandidate[]): MatchSelectSource => {
    if (!value) {
        return '';
    }
    if (value === NOT_IN_CENSUS_VALUE) {
        return NOT_IN_CENSUS_VALUE;
    }
    if (aiOptions.some((candidate) => candidate.id === value)) {
        return MATCH_SELECT_SOURCE_AI;
    }
    return MATCH_SELECT_SOURCE_DROPDOWN;
};

export const getResolvedCensusPatient = (
    match: ExtractedPatientMatch,
    disposition: MatchDisposition,
    selectedPatient?: CensusCandidate
): CensusCandidate | undefined => {
    // Only resolve a patient once the reviewer has confirmed — never surface the
    // system's best guess on Uncertain / unverified rows.
    if (disposition.status !== 'confirmed') {
        return undefined;
    }
    if (selectedPatient?.id === disposition.patientId) {
        return selectedPatient;
    }
    return getMatchSelectOptions(match).find((candidate) => candidate.id === disposition.patientId) ?? match.matchedPatient;
};

export const getMatchRowPatientName = (
    match: ExtractedPatientMatch,
    disposition: MatchDisposition,
    selectedPatient?: CensusCandidate
): string => {
    if (disposition.status === 'not-in-census') {
        return MATCH_NOT_IN_CENSUS_LABEL;
    }
    return getResolvedCensusPatient(match, disposition, selectedPatient)?.patientName ?? MATCH_SELECT_PATIENT_LABEL;
};

export const getMatchStatusChip = (match: ExtractedPatientMatch, disposition: MatchDisposition): MatchStatusChip => {
    if (disposition.status === 'not-in-census') {
        return { label: MATCH_NOT_IN_CENSUS_LABEL, color: 'default' };
    }
    if (disposition.status === 'confirmed') {
        return { label: MATCH_STATUS_CONFIRMED, color: 'success' };
    }
    if (match.confidence === 'high') {
        return { label: MATCH_STATUS_HIGH_CONFIDENCE, color: 'default' };
    }
    return { label: MATCH_STATUS_UNCERTAIN, color: 'warning' };
};

export const buildProceedPlan = (
    run: Pick<MatchVerificationRun, 'sessionId' | 'matches'>,
    dispositions: Record<string, MatchDisposition>
): MatchProceedPlan => {
    const writeToPatientRecord: string[] = [];
    const notInCensusMatchIds: string[] = [];
    const unverifiedMatchIds: string[] = [];

    run.matches.forEach((match) => {
        const disposition = dispositions[match.id] ?? { status: 'unverified' };
        if (disposition.status === 'confirmed') {
            writeToPatientRecord.push(disposition.patientId);
            return;
        }
        if (disposition.status === 'not-in-census') {
            notInCensusMatchIds.push(match.id);
            return;
        }
        unverifiedMatchIds.push(match.id);
    });

    return {
        sessionId: run.sessionId,
        writeToPatientRecord,
        notInCensusMatchIds,
        unverifiedMatchIds,
        carryToPdfMatchIds: [...notInCensusMatchIds, ...unverifiedMatchIds]
    };
};

/** Inputs used to prioritize Verify Matches rows for reviewer attention. */
export type MatchVerifySortInput = {
    dispositionStatus: MatchDisposition['status'];
    confidence: ExtractedPatientMatch['confidence'];
    /** Numeric score when available; `null`/`undefined` sorts as lowest confidence. */
    confidenceScore?: number | null;
    /** Stable tie-breaker from the API list order. */
    originalIndex: number;
};

/** Unverified rows surface first; confirmed / not-in-census fall below. */
const getMatchVerifyAttentionRank = (status: MatchDisposition['status']): number => (status === 'unverified' ? 0 : 1);

/**
 * Sort Verify Matches so uncertain / lower-confidence unverified patients appear
 * first. Verified rows sink below those still needing review; ties keep API order.
 */
export const compareMatchVerifySort = (a: MatchVerifySortInput, b: MatchVerifySortInput): number => {
    const attentionDiff = getMatchVerifyAttentionRank(a.dispositionStatus) - getMatchVerifyAttentionRank(b.dispositionStatus);
    if (attentionDiff !== 0) {
        return attentionDiff;
    }

    // Only prioritize by confidence among rows that still need review.
    if (a.dispositionStatus === 'unverified' && b.dispositionStatus === 'unverified') {
        const aUncertain = a.confidence === 'uncertain' ? 0 : 1;
        const bUncertain = b.confidence === 'uncertain' ? 0 : 1;
        if (aUncertain !== bUncertain) {
            return aUncertain - bUncertain;
        }

        const aScore = a.confidenceScore ?? Number.NEGATIVE_INFINITY;
        const bScore = b.confidenceScore ?? Number.NEGATIVE_INFINITY;
        if (aScore !== bScore) {
            return aScore - bScore;
        }
    }

    return a.originalIndex - b.originalIndex;
};

export const sortMatchVerifyRows = <T extends MatchVerifySortInput>(rows: T[]): T[] => [...rows].sort(compareMatchVerifySort);

export const formatCensusCandidateLabel = (candidate: { patientName: string; fin: string; unitLabel: string }): string =>
    [candidate.patientName, candidate.unitLabel, candidate.fin ? `FIN ${candidate.fin}` : ''].filter(Boolean).join(' · ');

export const HOSPITAL_CONTEXT_VERIFY_PATH = '/admin/hospitals/:hospitalId/meeting-transcription/:sessionId/verify';

export const getMatchVerifyPath = ({
    isAdminUser,
    hospitalId,
    sessionId,
    hospitalContext
}: {
    isAdminUser: boolean;
    hospitalId: string;
    sessionId: string;
    hospitalContext?: boolean;
}): string => {
    if (hospitalContext) {
        return `/admin/hospitals/${hospitalId}/meeting-transcription/${sessionId}/verify`;
    }
    return isAdminUser
        ? `/admin/meeting-transcription/${hospitalId}/${sessionId}/verify`
        : `/meeting-transcription/${hospitalId}/${sessionId}/verify`;
};

export const getMeetingTranscriptionPath = ({
    isAdminUser,
    hospitalId,
    hospitalContext
}: {
    isAdminUser: boolean;
    hospitalId?: string;
    hospitalContext?: boolean;
}): string => {
    if (hospitalContext && hospitalId) {
        return `/admin/hospitals/${hospitalId}/meeting-transcription`;
    }
    return isAdminUser ? '/admin/meeting-transcription' : '/meeting-transcription';
};

export const getMeetingTranscriptionUploadPath = ({
    isAdminUser,
    hospitalId,
    hospitalContext
}: {
    isAdminUser: boolean;
    hospitalId?: string;
    hospitalContext?: boolean;
}): string => {
    if (hospitalContext && hospitalId) {
        return `/admin/hospitals/${hospitalId}/meeting-transcription/upload`;
    }
    if (isAdminUser && hospitalId) {
        return `/admin/meeting-transcription/${hospitalId}/upload`;
    }
    return isAdminUser ? '/admin/meeting-transcription/upload' : '/meeting-transcription/upload';
};
